import React from 'react';
import { Zap, Clock, Lock, Globe, ArrowRight, PenLine, CheckCircle2, SlidersHorizontal, GripVertical, User, LayoutGrid, CreditCard, QrCode } from 'lucide-react';
import { Button } from './ui/button';

/**
 * Kiosk Direct — landing-page spotlight. Walk-ups → paid bookings in under a
 * minute on a dockside tablet. Copy left, live-flow mock right.
 */
export const KioskDirectSection = ({ onCta }) => (
  <section className="py-24 relative overflow-hidden bg-gradient-to-b from-white via-amber-50/40 to-white" data-testid="supplier-kiosk-direct-section">
    <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
          <div className="absolute top-1/4 -left-24 w-[420px] h-[420px] bg-amber-100/50 rounded-full blur-3xl" />
      <div className="absolute bottom-0 -right-32 w-[460px] h-[460px] bg-blue-100/40 rounded-full blur-3xl" />
    </div>

    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
        {/* Left — copy */}
        <div className="min-w-0">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-amber-50 border border-amber-500/25 mb-6">
            <Zap className="w-3.5 h-3.5 text-amber-500 fill-amber-400" />
            <span className="text-[11px] font-bold uppercase tracking-[0.14em] text-amber-700">Kiosk Direct · Walk-ins 2.0</span>
          </div>

          <h2 className="text-3xl sm:text-4xl lg:text-[2.75rem] font-bold tracking-tight leading-[1.08] text-gray-900 mb-5">
            Walk-ups become paid bookings — <span className="italic">in under a minute.</span>
          </h2>
          <p className="text-base sm:text-lg text-gray-600 leading-relaxed mb-8 max-w-xl">
            Prop a tablet at the dock. Guests type their details, tap a departure,
            pay with Stripe and sign the waiver — no app, no account, no queue at
            the counter.
          </p>

          <div className="space-y-3.5 mb-8">
            {[
              { icon: SlidersHorizontal, title: 'Customizable flows', desc: 'drag-and-drop the kiosk modules — guest details, services, waiver, checkout — into whatever order fits how your dock actually runs.' },
              { icon: Zap, title: 'Contact-first by default', desc: 'name, phone & email captured before the first tap — every walk-up becomes a lead, even if they bail.' },
              { icon: Clock, title: 'Live departures only', desc: 'shows what actually leaves soon, with real seat counts — and holds the spot while they pay.' },
              { icon: Lock, title: 'Pay & sign on the spot', desc: 'Stripe checkout hands straight into the waiver, sealed with a confirmation moment guests notice.' },
              { icon: Globe, title: 'Bilingual out of the box', desc: 'UAE operators get English + Arabic side by side across the entire flow.' },
            ].map((f) => (
              <div key={f.title} className="flex items-start gap-3.5">
                <span className="w-9 h-9 rounded-full bg-amber-50 text-amber-600 flex items-center justify-center shrink-0">
                  <f.icon className="w-4 h-4" strokeWidth={1.75} />
                </span>
                <div>
                  <span className="font-semibold text-gray-900 text-sm">{f.title}</span>
                  <span className="text-gray-500 text-sm"> — {f.desc}</span>
                </div>
              </div>
            ))}
          </div>

          <div className="flex flex-wrap items-center gap-4">
            <Button
              size="lg"
              onClick={onCta}
              className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white rounded-full px-7"
              data-testid="kiosk-direct-section-cta"
            >
              Start free — Kiosk Direct included <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
            <span className="text-xs text-gray-400 font-medium">Every plan · Any tablet · Zero setup</span>
          </div>
        </div>

        {/* Right — the flow, mocked (illustrative) */}
        <div className="relative min-w-0" aria-hidden="true">
          <div className="absolute -top-5 left-6 z-10 inline-flex items-center gap-2 px-3.5 py-2 rounded-full bg-white shadow-lg ring-1 ring-slate-900/[0.06]">
            <Zap className="w-4 h-4 text-amber-500 fill-amber-400" />
            <span className="text-xs font-bold text-slate-900">Kiosk Direct</span>
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
            </span>
          </div>

          <div className="rounded-[1.75rem] bg-white/85 backdrop-blur-sm ring-1 ring-slate-900/[0.05] shadow-[0_40px_80px_-30px_rgba(245,158,11,0.35)] p-5 sm:p-7">
            {/* Pegboard — your flow, your order */}
            <div
              className="rounded-2xl ring-1 ring-slate-900/[0.05] p-3.5 mb-3.5 overflow-x-auto"
              data-testid="kiosk-direct-pegboard-visual"
              style={{
                background: '#f5f8fc',
                backgroundImage: 'radial-gradient(circle, #d5deec 1.1px, transparent 1.1px)',
                backgroundSize: '14px 14px',
              }}
            >
              <div className="flex items-center justify-between mb-2.5 px-0.5">
                <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500">Build your flow</span>
                <span className="text-[10px] font-semibold text-[#2563EB] inline-flex items-center gap-1">
                  <GripVertical className="w-3 h-3" /> drag to reorder
                </span>
              </div>
              <div className="flex items-center gap-1 min-w-max">
                {[
                  { icon: User, label: 'Guest details' },
                  { icon: LayoutGrid, label: 'Services', lifted: true },
                  { icon: PenLine, label: 'Waiver' },
                  { icon: CreditCard, label: 'Checkout' },
                  { icon: QrCode, label: 'Check-in' },
                ].map((m, i, arr) => (
                  <React.Fragment key={m.label}>
                    <span
                      className={`inline-flex items-center gap-1.5 rounded-lg bg-white px-2.5 py-1.5 text-[10px] font-bold text-slate-700 whitespace-nowrap ${
                        m.lifted
                          ? 'ring-1 ring-[#2563EB] border border-dashed border-[#2563EB]/60 shadow-md -translate-y-0.5 -rotate-2 text-[#2563EB]'
                          : 'ring-1 ring-slate-900/[0.06] shadow-sm'
                      }`}
                    >
                      <m.icon className={`w-3 h-3 ${m.lifted ? 'text-[#2563EB]' : 'text-slate-400'}`} /> {m.label}
                    </span>
                    {i < arr.length - 1 && <ArrowRight className="w-3 h-3 text-[#2563EB]/50 shrink-0" />}
                  </React.Fragment>
                ))}
              </div>
              <p className="mt-2 px-0.5 text-[10px] text-slate-400">Waiver before payment or after — your call. QR check-in waits at the dock.</p>
            </div>

            {/* Contact — already typed */}
            <div className="rounded-2xl bg-white ring-1 ring-slate-900/[0.04] shadow-sm p-4 mb-3.5">
              <div className="flex items-center justify-between mb-2.5">
                <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400">Walk-in booking</div>
                <div className="text-[10px] font-semibold text-slate-300" dir="rtl" lang="ar">حجز فوري</div>
              </div>
              <div className="flex flex-wrap gap-2">
                {['Layla H.', '+971 50 ··· 4821', 'layla@…'].map((c) => (
                  <span key={c} className="text-[11px] font-medium text-slate-600 bg-slate-50 ring-1 ring-slate-900/[0.04] rounded-full px-2.5 py-1">{c}</span>
                ))}
                <span className="text-[11px] font-medium text-emerald-600 bg-emerald-50 rounded-full px-2.5 py-1 inline-flex items-center gap-1">
                  <CheckCircle2 className="w-3 h-3" /> consented
                </span>
              </div>
            </div>

            {/* Departure card */}
            <div className="rounded-2xl bg-white ring-1 ring-slate-900/[0.04] shadow-sm p-4 mb-3.5">
              <div className="flex items-baseline justify-between gap-3 mb-2.5">
                <span className="text-sm font-bold text-slate-900">Sunset Jet Ski</span>
                <span className="text-xs font-semibold text-slate-500">AED 300</span>
              </div>
              <div className="flex flex-wrap gap-2">
                <span className="text-[11px] font-bold text-white bg-[#2563EB] rounded-full px-3 py-1.5">4:30 PM · 3 left</span>
                <span className="text-[11px] font-semibold text-slate-600 ring-1 ring-slate-200 rounded-full px-3 py-1.5">5:15 PM · 6 left</span>
                <span className="text-[11px] font-semibold text-slate-300 bg-slate-50 rounded-full px-3 py-1.5">6:00 PM · <span className="text-red-400 font-bold">FULL</span></span>
              </div>
            </div>

            {/* Review + total */}
            <div className="rounded-2xl bg-white ring-1 ring-slate-900/[0.04] shadow-sm p-4 mb-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400">Review &amp; complete</span>
                <span className="text-[10px] font-semibold text-slate-300" dir="rtl" lang="ar">المراجعة والإتمام</span>
              </div>
              <div className="flex items-center justify-between text-[11px] text-slate-500 mb-1">
                <span>Today · 4:30 PM · 2 guests</span>
                <span>AED 600.00</span>
              </div>
              <div className="flex items-center justify-between text-[11px] text-slate-400 mb-2">
                <span>VAT (5%)</span>
                <span>Included</span>
              </div>
              <div className="flex items-center justify-between pt-2 border-t border-slate-100">
                <span className="text-xs font-bold text-slate-900">Total</span>
                <span className="text-base font-extrabold text-slate-900" style={{ fontVariantNumeric: 'tabular-nums' }}>AED 637.90</span>
              </div>
            </div>

            {/* Sticky checkout bar */}
            <div className="rounded-2xl bg-[#2563EB] text-white text-center text-sm font-bold py-3.5 shadow-[0_16px_32px_-14px_rgba(37,99,235,0.6)]">
              Complete booking · AED 637.90
            </div>

            {/* The hand-off */}
            <div className="mt-4 flex flex-wrap items-center gap-2">
              <span className="text-[11px] font-semibold text-emerald-700 bg-emerald-50 rounded-full px-2.5 py-1 inline-flex items-center gap-1">
                <CheckCircle2 className="w-3 h-3" /> Paid · BK-7F3A2C
              </span>
              <ArrowRight className="w-3 h-3 text-slate-300" />
              <span className="text-[11px] font-semibold text-blue-700 bg-blue-50 rounded-full px-2.5 py-1 inline-flex items-center gap-1">
                <PenLine className="w-3 h-3" /> Waiver signed
              </span>
              <span className="text-[11px] text-slate-400 ml-auto">under 60 seconds</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

export default KioskDirectSection;
